// Looping agent ringtone, built on the Web Audio API so there's no binary asset
// to ship or host. Plays a repeating two-beep "ring-ring" until stopped — used to
// alert an SDR that a chat (or call) contact is being offered, looping until they
// Accept or Reject it.
//
// Browser autoplay policy: an AudioContext created outside a user gesture starts
// `suspended`. Call primeRingtone() from a click (e.g. "Open softphone") to unlock
// it ahead of time; startRingtone() also attempts resume() as a best effort.

type AudioCtor = typeof AudioContext;

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let timer: ReturnType<typeof setInterval> | null = null;

// The ringtone has more than one independent owner: the app-wide SSE incoming-chat
// alert (<AgentNotifications>, owner 'sse') and the Workspace amazon-connect-streams
// voice-offer path (owner 'streams'). Reference-count them so one owner releasing
// (e.g. WorkspacePage unmounting with no active streams contact) can't silence a ring
// another owner still needs. The ring stops only when the LAST owner releases.
const owners = new Set<string>();

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctor: AudioCtor | undefined =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: AudioCtor }).webkitAudioContext;
  if (!Ctor) return null;
  if (!ctx) ctx = new Ctor();
  return ctx;
}

/** Unlock audio under the browser autoplay policy. Safe to call from a user gesture. */
export function primeRingtone(): void {
  const c = getCtx();
  if (c && c.state === 'suspended') void c.resume();
}

/** Schedule one "ring-ring" (two short beeps) starting now. */
function ring(): void {
  const c = ctx;
  if (!c || !master) return;
  const now = c.currentTime;
  for (const offset of [0, 0.24]) {
    const osc = c.createOscillator();
    const g = c.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(620, now + offset);
    osc.frequency.setValueAtTime(680, now + offset + 0.09);
    // Short attack/decay envelope so it sounds like a phone, not a drone.
    g.gain.setValueAtTime(0.0001, now + offset);
    g.gain.exponentialRampToValueAtTime(1, now + offset + 0.02);
    g.gain.setValueAtTime(1, now + offset + 0.16);
    g.gain.exponentialRampToValueAtTime(0.0001, now + offset + 0.2);
    osc.connect(g).connect(master);
    osc.start(now + offset);
    osc.stop(now + offset + 0.22);
  }
}

/**
 * Register `owner` as needing the ring and start the loop if it isn't already going.
 * Idempotent per owner. The loop keeps running until every owner has released.
 */
export function requestRingtone(owner: string): void {
  const c = getCtx();
  if (!c) return;
  if (c.state === 'suspended') void c.resume();
  owners.add(owner);
  if (timer !== null) return;
  if (!master) {
    master = c.createGain();
    master.gain.value = 0.2; // polite volume
    master.connect(c.destination);
  }
  ring();
  timer = setInterval(ring, 2200);
}

/** Release `owner`'s hold on the ring; stops the loop once no owner needs it. */
export function releaseRingtone(owner: string): void {
  owners.delete(owner);
  if (owners.size === 0 && timer !== null) {
    clearInterval(timer);
    timer = null;
  }
}

export function isRinging(): boolean {
  return timer !== null;
}
